/**
 * The editor's route tree, generated from the surface registry.
 *
 * Generated rather than written out: nineteen near-identical `RouteObject`
 * entries is what the flat editor had, and the registry is the one place a
 * section's address, its lease region and its legacy keys are stated.
 *
 * @module router/programme-editor/surface-routes
 */

import React from "react";

import { Navigate, type RouteObject, useLocation } from "react-router";

import { StudioSurface } from "@/components/studio/StudioSurface";
import { ProgrammeTeamRoute } from "@/router/programmes/ProgrammeTeamsRoutes";

import { GovernanceRoute } from "./GovernanceRoute";
import { HistoryRoute } from "./HistoryRoute";
import { legacyRedirects } from "./legacy-redirects";
import { SectionNav } from "./SectionNav";
import { SectionRoute } from "./SectionRoute";
import { FirstModuleRedirect, moduleCrumb, ModuleEditorRoute } from "./studio/ModuleEditorRoute";
import { SubmissionIndex } from "./submission/SubmissionIndex";
import { SurfaceLayout, useEditorBase } from "./SurfaceLayout";
import { SURFACE_INDEX, SURFACES } from "./surfaces";

/** The fragment readiness notifications carry to the governance panel. */
const GOVERNANCE_ANCHOR = "#readiness-and-approval";

/**
 * Forwards to an address under the editor's own base.
 *
 * Built from the base rather than with `..`: the legacy addresses are one and
 * two segments deep, so a relative hop would have to know which, and the
 * change case's base is two segments longer again.
 *
 * `replace`, so the old address does not sit in the history stack where Back
 * would land on it and forward again. The fragment is carried: a readiness
 * blocker's `#readiness-and-approval` is the whole point of the link.
 */
function EditorRedirect({ to }: { to: string }): React.ReactElement {
  const base = useEditorBase();
  const { hash, search } = useLocation();
  // That panel sat above every step then; it is a surface of its own now.
  const target = hash === GOVERNANCE_ANCHOR ? "governance" : to;
  // `to` may itself carry a query (e.g. `studio?tab=effort`, naming the tab a
  // collapsed legacy step now opens on): merge it with whatever the visited
  // address already had rather than concatenating two `?`s.
  const [path, targetQuery] = target.split("?");
  const params = new URLSearchParams(search);
  if (targetQuery) {
    new URLSearchParams(targetQuery).forEach((value, key) => params.set(key, value));
  }
  const query = params.toString();
  return <Navigate to={`${base}/${path}${query ? `?${query}` : ""}${hash}`} replace />;
}

/**
 * A surface with no sections of its own, given the walk every other surface
 * has: they are stops in the same order, so ending the walk before them would
 * leave the last two unreachable from the footer.
 */
function Standalone({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="space-y-4">
      {children}
      <SectionNav />
    </div>
  );
}

/**
 * A surface whose index is a screen of its own rather than the first of its
 * sections. The submission pack's is a container: it reads governance and
 * resolves a blocker's step key, neither of which the section registry can do.
 */
function indexElement(
  surface: (typeof SURFACES)[number],
  section: (typeof SURFACES)[number]["sections"][number],
): React.ReactElement {
  // Wrapped, not bare: the index is a stop in the walk like every other
  // section, and without the footer the walk dead-ends here.
  return surface.key === "submission" ? (
    <Standalone>
      <SubmissionIndex />
    </Standalone>
  ) : (
    <SectionRoute section={section} />
  );
}

/** What a surface renders when its own address is entered. */
function surfaceIndex(surface: (typeof SURFACES)[number]): RouteObject {
  return {
    index: true,
    element: <EditorRedirect to={`${surface.segment}/${SURFACE_INDEX[surface.key]}`} />,
  };
}

function surfaceRoute(surface: (typeof SURFACES)[number]): RouteObject {
  if (surface.key === "governance") {
    return {
      path: surface.segment,
      handle: { crumb: surface.title, structural: false, guide: surface.guide },
      element: (
        <Standalone>
          <GovernanceRoute />
        </Standalone>
      ),
    };
  }

  if (surface.key === "studio") {
    // The one surface addressed below its own segment: a module, then a tab
    // of that module's editor.
    return {
      path: surface.segment,
      handle: { crumb: surface.title, structural: false, guide: surface.guide },
      element: (
        <Standalone>
          <StudioSurface />
        </Standalone>
      ),
      children: [
        { index: true, handle: { redirect: true }, element: <FirstModuleRedirect /> },
        { path: ":moduleId", handle: { redirect: true }, element: <ModuleEditorRoute /> },
        {
          path: ":moduleId/:tab",
          handle: { crumb: moduleCrumb },
          element: <ModuleEditorRoute />,
        },
      ],
    };
  }

  if (surface.key === "history") {
    return {
      path: surface.segment,
      handle: { crumb: surface.title, structural: false },
      element: (
        <Standalone>
          <HistoryRoute />
        </Standalone>
      ),
    };
  }

  if (surface.key === "team") {
    return {
      path: surface.segment,
      handle: { crumb: surface.title, structural: false, guide: surface.guide },
      element: (
        <Standalone>
          <ProgrammeTeamRoute />
        </Standalone>
      ),
    };
  }

  return {
    path: surface.segment,
    handle: { crumb: surface.title, guide: surface.guide },
    element: <SurfaceLayout surface={surface} />,
    children: [
      // A surface whose first section has no segment of its own *is* the
      // index; every other one forwards to the section named for it.
      ...(surface.sections.some((section) => !section.segment) ? [] : [surfaceIndex(surface)]),
      ...surface.sections.map((section) =>
        section.segment
          ? {
              path: section.segment,
              handle: {
                crumb: section.title,
                structural: section.structural,
                guide: section.guide,
              },
              element: <SectionRoute section={section} />,
            }
          : {
              index: true,
              handle: {
                crumb: section.title,
                structural: section.structural,
                guide: section.guide,
              },
              element: indexElement(surface, section),
            },
      ),
    ],
  };
}

/** The children of both editor bases: the programme's, and a change case's. */
export function editorRoutes(): RouteObject[] {
  return [
    // The layout renders chrome and an outlet, so without an index its own
    // address drew the chrome around nothing. Bookmarks carry it.
    { index: true, element: <EditorRedirect to={`overview/${SURFACE_INDEX.overview}`} /> },
    ...SURFACES.map(surfaceRoute),
    // `redirect`, so the breadcrumb contract test can tell a route that
    // forwards from one that renders a screen and names no crumb.
    ...legacyRedirects().map((entry) => ({
      path: entry.from,
      handle: { redirect: true },
      element: <EditorRedirect to={entry.to} />,
    })),
  ];
}
